import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getStaffData, getSearchValue } from '../licenseAnalyticsSupport/getLicenseAnalyticsActions';
import { useQuery } from 'react-query';
import { ClubNameCell } from '../explorePageComponents/finance/academyTableComponent';
import { EmploymentStatus, employmentStatusNaming, LeadershipRole, leadershipRoleNaming, RegisteredPosition, registeredPositionNaming } from '../../../api/typesDc';
import { useTranslation } from 'react-i18next';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import '../licenseAnalyticsPage/explorePage.css'; // Import the CSS file
import { getClubDepartments } from '../../../api/club';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';

const StaffSubTable = React.memo(({ clubData, setSelectedRow }:any) => {
  const [t] = useTranslation();
  // Check if both lists are empty
  const hasNoData = !clubData?.competitionCareers?.length && !clubData?.coachingCareers?.length;

  // Helper function to render rows for a given career type
  const renderCareerRows = (careers:any, labelKey:any) => {
    if (careers && careers.length > 0) {
      return (
        <>
          <TableRow>
            <TableCell style={{ fontWeight: 'bold', padding: '5px 16px' }}>{t(labelKey)}</TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
          </TableRow>
          {careers.map((career:any, index:any) => (
            <TableRow key={index}>
              <TableCell style={{ padding: '2px 16px' }}>{career.club}</TableCell>
              <TableCell style={{ padding: '2px 16px' }}>{career.yearFrom}</TableCell>
              <TableCell style={{ padding: '2px 16px' }}>{career.yearTo}</TableCell>
              <TableCell style={{ padding: '2px 16px' }} />
            </TableRow>
          ))}
        </>
      );
    }
    return null; // Return null if there's no data for this category
  };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
        <div
          style={{
            backgroundColor: '#f3f6fb',
            fontSize: '16px',
            fontWeight: 'bold',
            paddingLeft: '16px',
            paddingBottom: '5px',
          }}
        >
          <ClubNameCell clubData={clubData} getImageUrl={(data:any) => data?.club?.imagePath} />
        </div>
        <div style={{ alignSelf: 'center', paddingRight: '10px' }}>
          <Tooltip title={'Close'}>
            <FontAwesomeIcon
              className="icon ml10"
              icon={faClose}
              style={{ cursor: 'pointer', fontSize: 18 }}
              onClick={() => setSelectedRow(null)}
            />
          </Tooltip>
        </div>
      </div>
      <Divider />
      <TableContainer className={'table-container'} sx={{ backgroundColor: '#f3f6fb', maxHeight: 500 }}>
        <Table stickyHeader aria-label="collapsible table">
          <TableHead>
            <TableRow>
              {/* <TableCell style={{ fontWeight: 'bold' }}>CLUB NAME</TableCell> */}
              <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', padding: '5px 16px' }}>
                {t('DC_5_CLUB')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', padding: '5px 16px' }}>
                {t('YEAR_FROM')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', padding: '5px 16px' }}>
                {t('YEAR_TO')}
              </TableCell>
              <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', padding: '5px 16px' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hasNoData ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                  {t('NO_DATA')}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {renderCareerRows(clubData?.competitionCareers, 'DC_5_COMPETITION_CAREER')}
                {renderCareerRows(clubData?.coachingCareers, 'DC_5_COACHING_CAREER')}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

const handleEmptyValue = (value:any) => (value ? value : '-');

const formatDate = (date:any) => {
  const d = new Date(date);

  if (isNaN(d.getTime())) return '-';

  const year = d.getFullYear().toString(); // Extract last two digits of the year
  const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
  const day = d
    .getDate()
    .toString()
    .padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const Row = React.memo(({ index, selectedRow, setSelectedRow, staff }:any) => {
  const [t] = useTranslation();

  // Convert and handle empty values
  const registeredPosition =
    staff?.registeredPosition?.map((item:RegisteredPosition) => t(registeredPositionNaming[item])).join(', ') || '-';
  const leadershipRole =
    staff?.leadershipRole
      ?.map((item:LeadershipRole) => t(leadershipRoleNaming[item])) // Translate each role name
      .filter((name:any) => name) // Filter out any empty or falsy values
      .join(', ') || '-'; // Join the remaining values, or default to '-'  
  const employmentStatus = [staff?.employmentStatus]?.map((item:EmploymentStatus) => t(employmentStatusNaming[item])).join(', ') || '-';

  // Fetch departments outside of Row to avoid multiple queries
  const { data: departments = [] }:any = useQuery(['departments', { clubId: staff?.club.id }], getClubDepartments, {
    enabled: !!staff?.club.id, // Only fetch if club id is present
  });

  const departmentNames = staff?.department?.map((el:any) => departments.find((d:any) => d.id == el)?.name).join(', ') || '-';

  return (
    <TableRow hover selected={index === selectedRow} onClick={() => setSelectedRow(index)}>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>
        <ClubNameCell clubData={staff} getImageUrl={(data:any) => data?.club?.imagePath} />
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{handleEmptyValue(staff?.kanjiName)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {handleEmptyValue(staff?.katakanaName)}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{handleEmptyValue(staff?.englishName)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{formatDate(staff?.dateOfBirth)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{handleEmptyValue(staff?.jfaId)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {handleEmptyValue(staff?.instructorRegistrationNumber)}
      </TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{employmentStatus}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{departmentNames}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{staff?.licenceStart ? formatDate(staff?.licenceStart) : '-'}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{staff?.licenceEnd ? formatDate(staff?.licenceEnd) : '-'}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{registeredPosition}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>{handleEmptyValue(staff?.jobTitle)}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '600px' }}>{leadershipRole}</TableCell>
      <TableCell style={{ fontSize: '0.75rem', padding: '2px 16px', minWidth: '100px', maxWidth: '375px' }}>
        {handleEmptyValue(staff?.annualSalary)}
      </TableCell>
    </TableRow>
  );
});

const StaffExplorePage = React.memo(() => {
  const [t] = useTranslation();
  const staffData = useSelector(getStaffData);
  const searchValue = useSelector(getSearchValue);

  const { data: departments = [], isLoading: isDepartmentsLoading } = useQuery(
    ['departments', { clubId: '3243' }],
    getClubDepartments,
    { staleTime: 60000 }, // Cache for 1 minute to avoid refetching
  );

  const [selectedRow, setSelectedRow] = useState(null);
  const [objectList, setObjectList] = useState(staffData);
  const [isAscending, setIsAscending] = useState(true);
    const [staffCount, setStaffCount] = useState();

  // Memoized sorting function
  const sortedObjectList = useMemo(() => {
    return [...objectList].sort((a, b) => {
      const nameA = a.club.name?.toLowerCase();
      const nameB = b.club.name?.toLowerCase();

      return isAscending ? nameA?.localeCompare(nameB) : nameB?.localeCompare(nameA);
    });
  }, [objectList, isAscending]);

  // Callback for sorting to avoid recreation on every render
  const handleSortClick = useCallback(() => {
    setIsAscending(prev => !prev);
  }, []);

  // Effect to handle filtering based on search value
  useEffect(() => {
    if (searchValue.trim() === '') {
      setObjectList(staffData);
      setStaffCount(staffData.length);
    } else {
      const filteredList = staffData.filter((item:any) => {
        const registeredPosition =
          item?.registeredPosition?.map((pos:RegisteredPosition) => t(registeredPositionNaming[pos])).join(', ') || '';
        const leadershipRole = item?.leadershipRole?.map((role:LeadershipRole) => t(leadershipRoleNaming[role])).join(', ') || '';
        const employmentStatus =
          [item?.employmentStatus]?.map((status:EmploymentStatus) => t(employmentStatusNaming[status])).join(', ') || '';
        const departmentName = item?.department?.map((id:any) => departments.find((d:any) => d.id === id)?.name).join(', ') || '';

        return [
          item.club.name,
          item.kanjiName,
          item.katakanaName,
          item.englishName,
          item.jfaId,
          item.instructorRegistrationNumber,
          item.jobTitle,
          registeredPosition,
          leadershipRole,
          employmentStatus,
          departmentName,
        ].some(field => field?.toLowerCase().includes(searchValue?.toLowerCase()));
      });
      setObjectList(filteredList);
      setStaffCount(filteredList?.length);
    }
  }, [searchValue, staffData, t, departments]);

  const rowsSkeleton = Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: 15 }).map((_, cellIndex) => (
        <TableCell key={cellIndex}>
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  ));

  return (
    <>
      <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontFamily: 'Montserrat', padding: '6px !important' }}>
        {t('DC_5_STAFF') + ' ' + t('DETAILS') + ` (${staffCount} ${t('ROWS')})`}
      </Typography>
      <Divider />
      <TableContainer className={'table-container'} sx={{ backgroundColor: '#f3f6fb', maxHeight: '600px' }}>
        <Table
          stickyHeader
          sx={{ backgroundColor: '#f3f6fb', width: 'max-content', boxShadow: 'none' }}
          size="small"
          aria-label="a dense table"
        >
          <TableRow className="sticky-header">
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', minWidth: '200px' }}>
              {t('DC_5_CLUB')}
              {!isAscending ? (
                <ArrowDropUpIcon onClick={handleSortClick} />
              ) : (
                <ArrowDropDownIcon onClick={handleSortClick} />
              )}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_KENJI_NAME')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_KATAKANA_NAME')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_ENGLISH_NAME')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_DOB')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_JFA_ID')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_IRN')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_EMPLOYMENT_STATUS')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DEPARTMENT')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_LICENCE_START_DATE')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_LICENCE_EXPIRY_DATE')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_REGISTERED_POSITION')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_JOB_TITLE')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', minWidth: '250px' }}>
              {t('DC_5_LEADERSHIP_ROLE')}
            </TableCell>
            <TableCell style={{ backgroundColor: '#f3f6fb', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('DC_5_ANNUAL_SALARY')}
            </TableCell>
          </TableRow>
          <TableBody>
            {staffData && staffData.length > 0 ? (
              objectList.length > 0 ? (
                sortedObjectList.map((staff, index) => (
                  <Row
                    key={index}
                    index={index}
                    selectedRow={selectedRow}
                    setSelectedRow={setSelectedRow}
                    staff={staff}
                  />
                ))
              ) : searchValue === '' ? (
                rowsSkeleton
              ) : (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                    {t('No_Matching_Records_Found')}...
                  </TableCell>
                </TableRow>
              )
            ) : staffData?.length == 0 ? (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  {t('No_Matching_Records_Found')}...
                </TableCell>
              </TableRow>
            ) : (
              rowsSkeleton
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {objectList.length > 0 && selectedRow !== null && (
        <div style={{ paddingTop: '20px', width: 'fit-content' }}>
          <StaffSubTable clubData={staffData[selectedRow]} setSelectedRow={setSelectedRow} />
        </div>
      )}
    </>
  );
});

export default StaffExplorePage;
